import { useFeature } from '@growthbook/growthbook-react';
import classNames from 'classnames';
import type React from 'react';
import FEATURES from '../../constants/featureToggling';
import { useElementRef } from '../../hooks/useElementRef';
import SliderArrows from '../SliderArrows/SliderArrows';
import styles from './CardsLine.module.css';
import { useColumnCount, useNavigationButton } from './hooks';
import { useLayoutEffect } from 'react';

const DEFAULT_TIMEOUT: number = 400;

interface Item {
  id: number | string;
}

interface Props<T extends Item = any>
  extends React.ComponentPropsWithoutRef<'div'> {
  items: T[];
  render(item: T, index: number): React.ReactElement;
  scrollable?: boolean;
  navigation?: boolean;
  listClassName?: string;
  itemClassName?: string;
  className?: string;
  limit?: number;
}

const CardsLine = ({
  items,
  scrollable = true,
  navigation = true,
  render,
  listClassName,
  itemClassName,
  className,
  limit,
}: Props): React.ReactElement => {
  const shouldUseThreeColumnsForGames = useFeature(
    FEATURES.USE_THREE_COLUMNS_FOR_GAMES
  ).on;

  const [componentElement, componentRef] = useElementRef<HTMLDivElement>();
  const [listElement, listRef] = useElementRef<HTMLUListElement>();

  const columnCount = useColumnCount(componentElement, scrollable);
  const count = limit ?? columnCount;
  const collection = scrollable ? items : items.slice(0, count);

  const { prevSlide, nextSlide, isPrevEnabled, isNextEnabled } =
    useNavigationButton(componentElement, listElement, items.length);

  useLayoutEffect(() => {
    let timeout = setTimeout(() => {
      listElement?.scrollTo?.({ left: 0 });
    }, DEFAULT_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [listElement]);

  return (
    <div
      ref={componentRef}
      className={classNames(
        styles.component_ig,
        shouldUseThreeColumnsForGames && styles.threeColumn,
        className
      )}
    >
      <ul
        ref={listRef}
        className={classNames(styles.list_ig, styles.scrollbar, listClassName)}
      >
        {collection.map((item, index) => (
          <li
            key={item.id}
            className={classNames(styles.item_ig, itemClassName)}
          >
            {render(item, index)}
          </li>
        ))}
      </ul>

      {navigation && scrollable && (
        <SliderArrows
          nextSlide={nextSlide}
          prevSlide={prevSlide}
          isNextEnabled={isNextEnabled}
          isPrevEnabled={isPrevEnabled}
          className={styles.navigation_ig}
        />
      )}
    </div>
  );
};

export default CardsLine;
